import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Download, RotateCcw, Eye, Settings, FileText, Clock, 
  CheckCircle, AlertCircle, Zap, Layers, FileVideo, FileAudio 
} from 'lucide-react';
import { MediaFile, ProcessingCommand } from '../types/media';

interface ProcessingPanelProps {
  uploadedFile: MediaFile;
  processedFile: MediaFile | null;
  onDownload: () => void;
  onReset: () => void;
  onPreview: () => void;
  isProcessing: boolean;
  commandHistory: ProcessingCommand[];
}

const ProcessingPanel: React.FC<ProcessingPanelProps> = ({
  uploadedFile,
  processedFile,
  onDownload,
  onReset,
  onPreview,
  isProcessing,
  commandHistory
}) => {
  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* File Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-900 rounded-lg border border-gray-800 p-4"
      >
        <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <FileText className="w-5 h-5" />
          <span>File Information</span>
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Type:</span>
            <div className="flex items-center space-x-1">
              {uploadedFile.type === 'video' ? (
                <FileVideo className="w-4 h-4" />
              ) : (
                <FileAudio className="w-4 h-4" />
              )}
              <span className="capitalize">{uploadedFile.type}</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Size:</span>
            <span>{formatFileSize(uploadedFile.size)}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Duration:</span>
            <span>{formatDuration(uploadedFile.duration)}</span>
          </div>
          
          {uploadedFile.type === 'video' && uploadedFile.metadata.width && (
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Resolution:</span>
              <span>{uploadedFile.metadata.width}×{uploadedFile.metadata.height}</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Processing Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gray-900 rounded-lg border border-gray-800 p-4"
      >
        <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <Zap className="w-5 h-5" />
          <span>Processing Status</span>
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Commands Applied:</span>
            <span className="font-medium">{commandHistory.length}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Status:</span>
            <div className="flex items-center space-x-2">
              {isProcessing ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full"
                  />
                  <span className="text-yellow-400">Processing</span>
                </>
              ) : processedFile ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-green-400">Completed</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-400">Ready</span>
                </>
              )}
            </div>
          </div>
          
          {processedFile && (
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Output Size:</span>
              <span>{formatFileSize(processedFile.size)}</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Command History */}
      <AnimatePresence>
        {commandHistory.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: 0.2 }}
            className="bg-gray-900 rounded-lg border border-gray-800 p-4"
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
              <Clock className="w-5 h-5" />
              <span>Command History</span>
            </h3>
            
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {commandHistory.map((command, index) => (
                <motion.div
                  key={command.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-800 rounded-lg p-3 border-l-4 border-blue-500"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{command.command}</span>
                    <span className="text-xs text-gray-400">
                      {new Date(command.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400">
                    Type: {command.type} • Status: {command.status}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="space-y-3"
      >
        {processedFile && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onDownload}
            className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 shadow-lg"
          >
            <Download className="w-5 h-5" />
            <span>Download Processed File</span>
          </motion.button>
        )}
        
        <div className="grid grid-cols-2 gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onPreview}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
          >
            <Eye className="w-4 h-4" />
            <span>Preview</span>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onReset}
            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Export Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gray-900 rounded-lg border border-gray-800 p-4"
      >
        <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <Settings className="w-5 h-5" />
          <span>Export Settings</span>
        </h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Quality
            </label>
            <select className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-white focus:outline-none">
              <option value="high">High Quality</option>
              <option value="medium">Medium Quality</option>
              <option value="low">Low Quality</option>
              {uploadedFile.type === 'video' && (
                <option value="4k">4K Quality</option>
              )}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Format
            </label>
            <select className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-white focus:outline-none">
              {uploadedFile.type === 'video' ? (
                <>
                  <option value="mp4">MP4</option>
                  <option value="avi">AVI</option>
                  <option value="mov">MOV</option>
                </>
              ) : (
                <>
                  <option value="mp3">MP3</option>
                  <option value="wav">WAV</option>
                  <option value="aac">AAC</option>
                </>
              )}
            </select>
          </div>
        </div>
      </motion.div>

      {/* Tips */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-gray-900 rounded-lg border border-gray-800 p-4"
      >
        <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
          <Layers className="w-5 h-5" />
          <span>Quick Tips</span>
        </h3>
        
        <div className="space-y-2 text-sm text-gray-400">
          <p>• Use natural language commands like "make it black and white"</p>
          <p>• Try voice commands by clicking the microphone button</p>
          <p>• You can apply multiple effects in sequence</p>
          <p>• Use the comparison view to see before/after results</p>
        </div>
      </motion.div>
    </div>
  );
};

export default ProcessingPanel;